import { useMutation } from '@apollo/client';
import { UploadFile } from 'antd/lib/upload/interface';
import { UPLOAD_FILE } from 'constants/file';
import { ERRORS } from 'constants/notify';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import FileAction from 'redux/file/file.actions';
import { DELETE_FILES } from 'services/graphql/mutations/files';
import { GeneralFunction } from 'types/common';
import { defaultNotification } from 'utils';
import { uploadFileProgressEvent } from 'utils/realtimeUpdate';

import { IDocument, IFile } from '../types/file';

interface IUploadTransactionAppointmentFile extends Partial<IDocument<UploadFile & Partial<IFile>>> {
  callbacks?: { success?: GeneralFunction; fail?: GeneralFunction };
  transactionId?: string;
  appointmentIds?: string[];
  existedFiles?: IFile[];
}
interface IUseUploadTransactionAppointmentFileProps {
  showPercentage?: boolean;
}

export default function useUploadTransactionAppointmentFile(props?: IUseUploadTransactionAppointmentFileProps) {
  const [uploadPercentage, setUploadPercentage] = useState<number | undefined>(0);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [deleteFiles] = useMutation(DELETE_FILES);
  // const uploadPercentageThreshold = 75;

  useEffect(() => {
    if (!props?.showPercentage) return () => {};
    const fileUploadListener = (percent: number) => {
      // if (uploadPercentage < uploadPercentageThreshold)
      //   setUploadPercentage(percent >= uploadPercentageThreshold ? uploadPercentageThreshold : percent);
      setUploadPercentage(percent);
    };
    uploadFileProgressEvent.addListener(UPLOAD_FILE, fileUploadListener);
    return () => {
      uploadFileProgressEvent.removeListener(UPLOAD_FILE, fileUploadListener);
    };
  }, []);

  return {
    uploadDocument: async function uploadDocument({
      details,
      files,
      transactionId,
      callbacks,
      subType,
      appointmentIds,
      existedFiles,
    }: IUploadTransactionAppointmentFile) {
      const callbackOnSuccess = (...args: any) => {
        setLoading(false);
        setUploadPercentage(0);
        callbacks?.success?.(...args);
      };

      const callbackOnFailure = (...args: any) => {
        setLoading(false);
        setUploadPercentage(0);
        callbacks?.fail?.(...args);
      };

      const fileIds: Record<string, boolean> = {};
      const newUploadFiles =
        files?.filter((f) => {
          if (f.id || f.uid) {
            fileIds[f.id || f.uid] = true;
          }
          return !!f.originFileObj;
        }) || [];
      const uploadDocumentData = {
        details: details ? JSON.stringify(details) : undefined,
        files: newUploadFiles?.map((f) => (f.originFileObj || f) as any) || [],
        transactionId: transactionId || '',
        subType,
      };
      const deletedFiles = existedFiles?.reduce((acc, existedFile) => {
        const existedFileId = existedFile.id || '';
        if (!fileIds[existedFileId]) {
          acc.push(existedFileId);
        }
        return acc;
      }, [] as string[]);

      try {
        if (deletedFiles?.length) {
          setLoading(true);
          await deleteFiles({ variables: { ids: deletedFiles } });
        }
        if (uploadDocumentData.files?.length > 0) {
          setLoading(true);
          dispatch(
            FileAction.uploadDocumentStart(
              {
                ...uploadDocumentData,
                ...(appointmentIds ? { appointmentIds: JSON.stringify(appointmentIds) } : {}),
              },
              {
                callbackOnSuccess,
                callbackOnFailure,
              },
            ),
          );
        } else {
          callbackOnSuccess();
        }
      } catch (err) {
        defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
        setLoading(false);
        callbackOnFailure(err);
      }
    },
    uploadPercentage,
    loading,
  };
}
